import { LightningElement, api, wire } from "lwc";
import { c as classNames } from "tds/chunk_index";
import "@devforce/trailhead-client-logger";
import { assignedNodes } from "common/slot";
import { LabelsAdapter } from "common/labelsAdapter";
import { createTrack } from "common/track";
import { attrToNumber, attrToBool } from "common/helpers";
import { ResizeObserverAdapter } from "common/resizeObserverAdapter";

var data$9 = { showMore: "Mehr anzeigen", showLess: "Weniger anzeigen" };
data$9.showMore;
data$9.showLess;

var data$8 = { showMore: "Show More", showLess: "Show Less" };
data$8.showMore;
data$8.showLess;

var data$7 = { showMore: "Mostrar más", showLess: "Mostrar menos" };
data$7.showMore;
data$7.showLess;

var data$6 = { showMore: "Mostrar más", showLess: "Mostrar menos" };
data$6.showMore;
data$6.showLess;

var data$5 = { showMore: "Afficher plus", showLess: "Afficher moins" };
data$5.showMore;
data$5.showLess;

var data$4 = { showMore: "Mostra altro", showLess: "Mostra meno" };
data$4.showMore;
data$4.showLess;

var data$3 = { showMore: "表示件数を増やす", showLess: "表示件数を減らす" };
data$3.showMore;
data$3.showLess;

var data$2 = { showMore: "더 보기", showLess: "덜 보기" };
data$2.showMore;
data$2.showLess;

var data$1 = { showMore: "Mostrar mais", showLess: "Mostrar menos" };
data$1.showMore;
data$1.showLess;

var data = { showMore: "显示更多", showLess: "显示更少" };
data.showMore;
data.showLess;

var locales = /*#__PURE__*/ Object.freeze({
  __proto__: null,
  de: data$9,
  en: data$8,
  es: data$7,
  esMX: data$6,
  fr: data$5,
  it: data$4,
  ja: data$3,
  ko: data$2,
  ptBR: data$1,
  zhCN: data,
});

class truncate extends LightningElement {
  @api assistiveText = null;
  @api displayMode = "clamp";
  @api lines = 3;
  @api lineSize = 16 * 1.3; // rem * approx line-height;

  @api linkHref = null;
  @api linkLabel = null;
  @api linkTarget = null;
  @api type = "expand";
  titleText = null;
  track = createTrack(this);

  get computedClassName() {
    return classNames("truncate__content", {
      "truncate__content--expanded": this.expanded,
      "truncate__content--clamp": this.isClampMode,
      "truncate__content--fade":
        !this.isClampMode && this.truncated && !this.expanded,
    });
  }

  get computedMaxHeight() {
    return Math.ceil(attrToNumber(this.lines) * attrToNumber(this.lineSize));
  }

  get computedStyle() {
    return `--clamp-size: ${this.lines}; --max-height: ${this.computedMaxHeight}px;`;
  }

  get isClampMode() {
    return this.displayMode !== "fade";
  }

  truncated = false;
  _expanded = false;

  @api get expanded() {
    return this._expanded;
  }

  set expanded(value) {
    this._expanded = attrToBool(value);
  }

  @wire(LabelsAdapter, {
    locales,
  })
  labels = {};

  get buttonLabel() {
    return (
      this.linkLabel ||
      (this.expanded ? this.labels.showLess : this.labels.showMore)
    );
  }

  get shouldShowButton() {
    return this.type !== "hide" && this.truncated;
  }

  get showReadOnlyButton() {
    return this.shouldShowButton && this.type === "readonly";
  }

  get showButtonLink() {
    return (
      this.shouldShowButton &&
      !this.showReadOnlyButton &&
      !!this.linkHref &&
      !!this.linkLabel
    );
  }

  get showButton() {
    return (
      this.shouldShowButton && !this.showReadOnlyButton && !this.showButtonLink
    );
  }

  get ariaTitle() {
    return this.assistiveText || this.titleText;
  }

  handleSlotChange() {
    const nodes = assignedNodes(this.template.querySelector("slot")); // Build title attribute text when in "hide" mode

    if (this.type === "hide") {
      this.titleText = nodes
        .map((n) => n.textContent?.trim())
        .join("\n")
        .trim();
    } // Elements can have unpredicatble box model properties so warn if attempting to clamp HTML
  }

  handleClick() {
    switch (this.type) {
      case "event":
        this.dispatchEvent(new CustomEvent("expand"));
        break;

      case "expand":
        this.expanded = !this.expanded;
        break;
    }

    this.track("link_clicked", {
      label: this.buttonLabel,
      href: this.linkHref,
    });
  }

  @wire(ResizeObserverAdapter, {
    template: "$template",
  })
  onResize(entry) {
    const { height } = entry.contentRect; // Return early if we can easily determine that content doesn't need truncation

    if (!this.isClampMode && height < this.computedMaxHeight) {
      this.expanded = false;
      this.truncated = false;
      return;
    } // Determine if CSS clamp styles are applied and if they can be measured

    const canUseClampedStyles = this.isClampMode && !this.expanded;

    if (canUseClampedStyles) {
      const container = this.template.querySelector(".truncate__content");
      this.truncated = container
        ? container.clientHeight < container.scrollHeight
        : false;
    } else {
      const buffer = this.isClampMode ? attrToNumber(this.lineSize) * 2 : 0;
      this.truncated = height - buffer > this.computedMaxHeight;

      if (!this.truncated) {
        this.expanded = false;
      }
    }
  }
}

export default truncate;
